import React, { useState } from 'react';
import styles from './Login.module.css';

// Form state + submit for a login screen, shared by this card and the V2
// login (src/v2/LoginV2.js) so both sign in exactly the same way. Posts
// username + password to /api/auth/login; on success, the server sets the
// pp_session cookie and the parent App refreshes its auth state via onSuccess().
export function useLoginForm(onSuccess) {
  const [user,     setUser]     = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setErrorText] = useState(null);
  // Bumped on every failed attempt — the same message twice in a row is two
  // failures, which `error` alone can't show (V2 replays its animation on it).
  const [failures, setFailures] = useState(0);
  const setError = (text) => {
    setErrorText(text);
    if (text) setFailures(n => n + 1);
  };

  async function submit(e) {
    e.preventDefault();
    if (!user.trim() || !password) {
      setError('Enter your username and password.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ user: user.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Login failed.');
        return;
      }
      onSuccess(data);
    } catch (err) {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return { user, setUser, password, setPassword, submitting, error, failures, submit };
}

// Login card shown when /api/auth/me returns 401.
export default function Login({ onSuccess }) {
  const { user, setUser, password, setPassword, submitting, error, submit } = useLoginForm(onSuccess);

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <img src="/pinpoint.png" alt="Pinpoint" />
        </div>
        <h1 className={styles.title}>Sign in to Pinpoint Mail</h1>
        <p className={styles.subtitle}>Use your OnPoint username and password.</p>
        <form className={styles.form} onSubmit={submit} autoComplete="on">
          <div className={styles.field}>
            <label htmlFor="login-user">Username or email</label>
            <input
              id="login-user"
              type="text"
              value={user}
              onChange={e => setUser(e.target.value)}
              autoComplete="username"
              autoFocus
              disabled={submitting}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={submitting}
            />
          </div>
          {error && <div className={styles.error}>{error}</div>}
          <button type="submit" className={styles.submit} disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className={styles.hint}>
          Trouble signing in? Contact Darren.
        </div>
      </div>
    </div>
  );
}
